import { z } from "zod";
import { PTO_ENTRY_STATUSES, PTO_ENTRY_TYPES } from "@/lib/enums";

/**
 * Zod schemas for the PTO Entry entity. Dates are plain "YYYY-MM-DD" strings
 * end to end (form -> API -> DB, mode: "string" on the Drizzle column) — no
 * Date object ever crosses this boundary, so there's no timezone drift.
 *
 * `updatePtoEntrySchema` is built from the un-refined base object BEFORE the
 * cross-field `endDate >= startDate` check, because Zod's `.partial()` does
 * not compose cleanly through a `.refine()`-wrapped (ZodEffects) schema.
 */

const baseObject = z.object({
  startDate: z.iso.date(),
  endDate: z.iso.date(),
  // Partial-day override; omitted/null means "full day(s) for the range".
  totalHours: z.number().min(0, "Hours cannot be negative").max(24, "Hours is too high").nullish(),
  type: z.enum(PTO_ENTRY_TYPES),
  // Required (not defaulted here, unlike the DB column's default) — the form
  // always sets it via defaultValues, and the API always receives it explicitly.
  status: z.enum(PTO_ENTRY_STATUSES),
  note: z.string().max(2000, "Note is too long").nullish(),
});

function refineDateOrder<T extends z.ZodType<{ startDate: string; endDate: string }>>(schema: T) {
  return schema.refine((data) => data.endDate >= data.startDate, {
    message: "End date must be on or after the start date",
    path: ["endDate"],
  });
}

// What the API accepts on POST — and what the create/edit form collects
// directly (no unit conversion needed here, unlike widget's cents/dollars, so
// one schema serves both boundaries).
export const createPtoEntrySchema = refineDateOrder(baseObject);
export type PtoEntryFormValues = z.infer<typeof createPtoEntrySchema>;

// PUT is a partial update — every field optional. Only re-checks date order
// when both dates are present in the same request body.
export const updatePtoEntrySchema = baseObject
  .partial()
  .refine(
    (data) =>
      data.startDate === undefined || data.endDate === undefined || data.endDate >= data.startDate,
    { message: "End date must be on or after the start date", path: ["endDate"] },
  );

export type CreatePtoEntryInput = z.infer<typeof createPtoEntrySchema>;
export type UpdatePtoEntryInput = z.infer<typeof updatePtoEntrySchema>;
